import { z } from 'zod';

/**
 * O WhatsApp da igreja (M13). O número que envia é o dela — nunca um número
 * compartilhado da plataforma.
 */

export const WHATSAPP_STATUS = [
  'DISCONNECTED',
  'QR_PENDING',
  'CONNECTING',
  'CONNECTED',
  'EXPIRED',
  'ERROR',
] as const;
export const whatsappStatusSchema = z.enum(WHATSAPP_STATUS);
export type WhatsappStatus = z.infer<typeof whatsappStatusSchema>;

export const WHATSAPP_STATUS_LABEL: Record<WhatsappStatus, string> = {
  DISCONNECTED: 'Desconectado',
  QR_PENDING: 'Aguardando leitura do QR',
  CONNECTING: 'Conectando',
  CONNECTED: 'Conectado',
  EXPIRED: 'Sessão expirada',
  ERROR: 'Com erro',
};

/** O que a igreja precisa fazer agora, para cada estado. */
export const WHATSAPP_STATUS_AJUDA: Record<WhatsappStatus, string> = {
  DISCONNECTED: 'Conecte o WhatsApp da igreja para enviar por este canal.',
  QR_PENDING: 'Abra o WhatsApp no celular da igreja: Aparelhos conectados → Conectar aparelho.',
  CONNECTING: 'Pareando com o WhatsApp. Isso leva alguns segundos.',
  CONNECTED: 'Tudo certo. As mensagens saem pelo número da igreja.',
  EXPIRED: 'A sessão caiu. Leia o QR de novo para reconectar.',
  ERROR: 'Algo deu errado na conexão. Tente gerar um novo código.',
};

export const whatsappConnectionSchema = z.object({
  status: whatsappStatusSchema,
  /** Texto do QR, para a tela desenhar. Só existe enquanto aguarda leitura. */
  qrCode: z.string().nullable(),
  qrExpiresAt: z.string().datetime().nullable(),
  phoneNumber: z.string().nullable(),
  profileName: z.string().nullable(),
  profilePictureUrl: z.string().nullable(),
  connectedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  /** Quantas mensagens já saíram hoje, e o teto do plano. */
  sentToday: z.number().int(),
  dailyLimit: z.number().int(),
  /** `fake` grava no banco em vez de enviar — desenvolvimento, testes e CI. */
  simulado: z.boolean(),
});
export type WhatsappConnection = z.infer<typeof whatsappConnectionSchema>;

export const WHATSAPP_MESSAGE_STATUS = [
  'QUEUED',
  'SENT',
  'DELIVERED',
  'READ',
  'FAILED',
  'RECEIVED',
] as const;
export const whatsappMessageStatusSchema = z.enum(WHATSAPP_MESSAGE_STATUS);
export type WhatsappMessageStatus = z.infer<typeof whatsappMessageStatusSchema>;

export const WHATSAPP_MESSAGE_STATUS_LABEL: Record<WhatsappMessageStatus, string> = {
  QUEUED: 'Na fila',
  SENT: 'Enviada',
  DELIVERED: 'Entregue',
  READ: 'Lida',
  FAILED: 'Falhou',
  RECEIVED: 'Recebida',
};

export const whatsappMessageSchema = z.object({
  id: z.string().uuid(),
  direction: z.enum(['IN', 'OUT']),
  phone: z.string(),
  personName: z.string().nullable(),
  body: z.string(),
  status: whatsappMessageStatusSchema,
  error: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type WhatsappMessage = z.infer<typeof whatsappMessageSchema>;

export const sendTestMessageSchema = z.object({
  /** Para onde mandar o teste. Vazio = o telefone de quem está pedindo. */
  phone: z.string().trim().max(20).nullish(),
});
export type SendTestMessageInput = z.infer<typeof sendTestMessageSchema>;

/** Comandos que quem serve pode responder por mensagem (M13). */
export const WHATSAPP_COMANDOS = {
  confirmar: ['1', 'sim', 'confirmo', 'confirmado', 'vou'],
  recusar: ['2', 'nao', 'não', 'nao posso', 'não posso'],
  sair: ['sair', 'parar', 'cancelar', 'stop'],
  ajuda: ['ajuda', 'menu', '?'],
} as const;

export const whatsappOptoutSchema = z.object({
  phone: z.string(),
  personName: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type WhatsappOptout = z.infer<typeof whatsappOptoutSchema>;

/**
 * Controle do simulador (`WHATSAPP_DRIVER=fake`). Existe só quando o driver é
 * o simulado: em produção a rota responde 404, e a tela não mostra o painel.
 * É o que permite ver — e testar — pareamento, queda e resposta sem um número
 * de verdade.
 */
export const SIMULATE_WHATSAPP_ACTIONS = ['parear', 'queda', 'mensagem'] as const;
export const simulateWhatsappActionSchema = z.enum(SIMULATE_WHATSAPP_ACTIONS);
export type SimulateWhatsappAction = z.infer<typeof simulateWhatsappActionSchema>;

export const simulateWhatsappSchema = z.object({
  acao: simulateWhatsappActionSchema,
  /** Para `mensagem`: de qual número veio, e o que dizia. */
  phone: z.string().trim().max(20).nullish(),
  texto: z.string().trim().max(500).nullish(),
});
export type SimulateWhatsappInput = z.infer<typeof simulateWhatsappSchema>;
